import { useEffect, useState } from "react";
import { Alert, Button, Paper, styled, Typography } from "@mui/material";
import { useQueryClient } from "react-query";

import { useUuidProgressIdContext } from "hooks/routerHooks";
import { useNavigate } from "hooks/routerHooks";
import { useComputationProgressStudyArea } from "hooks/studyAreaHooks";

import { LinearProgressWithLabel } from "components/loader/LinearProgressWithLabel";
import { useTranslation } from "react-i18next";
import { ComputationType } from "types/types";
import { deserializeErrors } from "utils/ErrorUtils";
import { NavLink } from "components/navigation/NavLink";
import { MainStyles } from "layout/MainStyles.";

export function ProgressPage() {
  const { progressId, uuid } = useUuidProgressIdContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [computationStatus, setComputationStatus] = useState<
    ComputationType | undefined
  >();

  const isError = computationStatus === "FAILED";

  const { data: progressStatus } = useComputationProgressStudyArea(
    uuid,
    progressId,
    {
      enabled:
        computationStatus !== "FINISHED" && computationStatus !== "FAILED",
    }
  );
  const errorList = deserializeErrors(progressStatus?.errorMessageList);

  useEffect(() => {
    if (computationStatus === "FINISHED") {
      queryClient.invalidateQueries(["studyareas", uuid]);
      return navigate(`/${uuid}/result`);
    }
    if (computationStatus === "FAILED") return;
    setComputationStatus(progressStatus?.computationStatus);
  }, [progressStatus, uuid, navigate, computationStatus, queryClient]);

  return (
    <Styles>
      <Paper className={`${isError ? "error" : ""} center-position`}>
        {!isError && (
          <Typography variant="h2">
            {t("label.patienceWeAreCalculatingTheResult")}
          </Typography>
        )}
        {isError && (
          <Typography color="error" variant="h2">
            {t("label.somethingWentWrong")}
          </Typography>
        )}
        <LinearProgressWithLabel
          value={progressStatus?.percentage || 0}
          color={isError ? "error" : "primary"}
          error={isError}
        />
        {isError && (
          <Alert severity="error">
            {errorList.map((error) => error.message)}
          </Alert>
        )}
        {isError && (
          <Button
            variant="outlined"
            component={NavLink}
            to={`/${uuid}/measures`}
          >
            {t("label.back")}
          </Button>
        )}
      </Paper>
    </Styles>
  );
}

const Styles = styled(MainStyles)`
  .center-position {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
    padding: 16px;
    min-width: ${({ theme }) => theme.spacing(50)};
  }
`;
